import axios from "axios";
import logger from "../../utils/logger";
import { captureError } from "../../utils/sentry";

// REACT_APP_API_URL must be set in Vercel environment variables to point at
// the Render backend (e.g. https://api.sportifide.com/api).
// The hostname-based fallback below only makes sense for local development
// (when both frontend and backend run on the same machine).  In production
// the hostname is the Vercel domain which has no backend on port 5000, so we
// intentionally leave the fallback undefined so the missing env var is obvious
// in the console rather than silently targeting the wrong host.
const _rawApiUrl =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" &&
  window.location?.hostname &&
  window.location.hostname === "localhost"
    ? `http://localhost:5000/api`
    : undefined);

if (!_rawApiUrl) {
  console.error(
    "[apiService] REACT_APP_API_URL is not set. " +
      "Add it to your Vercel environment variables: " +
      "REACT_APP_API_URL=https://api.sportifide.com/api"
  );
}

// Ensure the URL is always absolute (guard against missing https:// in env var)
const API_BASE_URL = _rawApiUrl
  ? _rawApiUrl.startsWith("http://") || _rawApiUrl.startsWith("https://")
    ? _rawApiUrl
    : `https://${_rawApiUrl}`
  : "https://api.sportifide.com/api"; // hard-coded production fallback

// Create axios instance with CORS support
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 seconds timeout
  withCredentials: true, // Enable credentials for CORS
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

// CSRF Token Management
let csrfToken = null;
// Shared in-flight promise — prevents multiple concurrent /auth/csrf-token requests
// when many POST calls hit a 403 at the same time.
let _csrfFetchPromise = null;

/**
 * Fetch CSRF token from server.
 * De-duplicates concurrent calls: all callers share the same in-flight promise.
 */
const fetchCsrfToken = async () => {
  if (_csrfFetchPromise) return _csrfFetchPromise;
  _csrfFetchPromise = axios
    .get(`${API_BASE_URL}/auth/csrf-token`, { withCredentials: true })
    .then((response) => {
      csrfToken = response.data?.data?.csrfToken;
      logger.debug("CSRF token fetched successfully");
      return csrfToken;
    })
    .catch((error) => {
      logger.warn("Failed to fetch CSRF token", { error: error.message });
      return null;
    })
    .finally(() => {
      _csrfFetchPromise = null;
    });
  return _csrfFetchPromise;
};

/**
 * Get current CSRF token (fetch if not available)
 */
const getCsrfToken = async () => {
  if (!csrfToken) {
    await fetchCsrfToken();
  }
  return csrfToken;
};

// Fetch CSRF token on app initialization
fetchCsrfToken();

apiClient.interceptors.request.use(
  async (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };

    // Add CSRF token to state-changing requests
    if (["POST", "PUT", "DELETE", "PATCH"].includes(config.method?.toUpperCase())) {
      const token = await getCsrfToken();
      if (token) {
        config.headers["X-CSRF-Token"] = token;
      }
    }

    // Fallback: if a token exists in localStorage (local dev, non-cookie env),
    // attach it as Authorization header. Production relies on HttpOnly cookie
    // which axios sends automatically via withCredentials:true.
    const localToken = localStorage.getItem("token");
    if (localToken && !config.headers["Authorization"]) {
      config.headers["Authorization"] = `Bearer ${localToken}`;
    }

    logger.debug("API Request", {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      data: config.data ? "Present" : "None",
      headers: localToken ? "Bearer token + CSRF" : "Cookie-based auth (HttpOnly) + CSRF token",
    });

    return config;
  },
  (error) => {
    logger.error("API Request Error", { error: error.message });
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    const duration = Date.now() - response.config.metadata.startTime;

    logger.logApiCall(
      response.config.method?.toUpperCase(),
      response.config.url,
      response.status,
      duration,
      {
        responseSize: JSON.stringify(response.data).length,
      }
    );

    return response;
  },
  async (error) => {
    const duration = error.config?.metadata
      ? Date.now() - error.config.metadata.startTime
      : 0;

    const status = error.response?.status || 0;
    const message = error.response?.data?.message || error.message;

    logger.logApiCall(
      error.config?.method?.toUpperCase() || "UNKNOWN",
      error.config?.url || "Unknown URL",
      status,
      duration,
      {
        error: message,
        errorType: error.code || "API_ERROR",
      }
    );

    // Handle 403 Forbidden - CSRF token validation failed
    if (status === 403 && error.response?.data?.code === "CSRF_VALIDATION_FAILED") {
      logger.warn("CSRF validation failed, refetching token");
      
      // Refetch CSRF token and retry
      try {
        await fetchCsrfToken();
        
        error.config._retryCount = (error.config._retryCount || 0) + 1;
        
        // Only retry once
        if (error.config._retryCount <= 1) {
          // Add new CSRF token to retry request
          const newToken = await getCsrfToken();
          if (newToken) {
            error.config.headers["X-CSRF-Token"] = newToken;
          }
          
          return apiClient(error.config);
        }
      } catch (csrfError) {
        logger.error("CSRF token refresh and retry failed", { error: csrfError.message });
        return Promise.reject(error);
      }
    }

    // Handle 401 Unauthorized - token expired
    // BUT: Don't try to refresh on auth endpoints themselves (login, register, refresh)
    const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/google', '/auth/facebook', '/auth/verify-otp'];
    const isAuthEndpoint = authEndpoints.some(endpoint => error.config?.url?.includes(endpoint));
    
    if (status === 401 && !error.config._retry && !isAuthEndpoint) {
      error.config._retry = true;

      try {
        // Call refresh endpoint to get new token
        // The new token will be set in HttpOnly cookie automatically
        const refreshResponse = await apiClient.post("/auth/refresh");

        if (refreshResponse.status === 200) {
          // Retry original request with new token
          return apiClient(error.config);
        }
      } catch (refreshError) {
        const refreshStatus = refreshError.response?.status;
        const refreshFailure = {
          status: refreshStatus,
          reason: refreshError.response?.data?.reason,
          requestId: refreshError.response?.data?.requestId || refreshError.response?.headers?.["x-request-id"],
          originalUrl: error.config?.url,
        };
        logger.error("Token refresh failed", { error: refreshError.message, ...refreshFailure });
        // An expired session (401/403) is normal; a 5xx or no answer means
        // sign-in is broken for people, so it goes to Sentry (when on)
        if (refreshStatus !== 401 && refreshStatus !== 403) {
          captureError(new Error(`Token refresh failed: ${refreshStatus || "no response"}`), refreshFailure);
        }
        
        // Only clear storage and redirect if refresh truly failed (not a network error)
        // AND we're not already on the login page
        const isOnLoginPage = window.location.pathname === '/login';
        
        if ((refreshStatus === 401 || refreshStatus === 403) && !isOnLoginPage) {
          console.warn('⚠️ Refresh token invalid, clearing storage and redirecting to login');
          logger.warn("Refresh token invalid, clearing storage and redirecting to login");
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = "/login";
        } else if (isOnLoginPage) {
          console.log('ℹ️ Already on login page, not redirecting');
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // If this is a 401 on auth endpoint (login/register failed), don't redirect
    // Just return the error so the UI can handle it
    if (status === 401 && isAuthEndpoint) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const apiService = {
  async registerUser(userData) {
    try {
      logger.logUserAction("register_attempt", { email: userData.email });
      const response = await apiClient.post("/users/register", userData);
      logger.logUserAction("register_success", { email: userData.email });
      return response.data;
    } catch (error) {
      logger.logUserAction("register_failed", {
        email: userData.email,
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  async loginUser(userData) {
    try {
      logger.logUserAction("login_attempt", { email: userData.email });
      const response = await apiClient.post("/auth/login", userData);
      logger.logUserAction("login_success", { email: userData.email });
      return response.data;
    } catch (error) {
      logger.logUserAction("login_failed", {
        email: userData.email,
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  async getUserProfile() {
    try {
      const response = await apiClient.get("/users/profile");
      logger.logUserAction("profile_fetched");
      return response.data;
    } catch (error) {
      logger.logUserAction("profile_fetch_failed", {
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  async updateUserProfile(profileData) {
    try {
      logger.logUserAction("profile_update_attempt");
      const response = await apiClient.put("/users/profile", profileData);
      logger.logUserAction("profile_update_success");
      return response.data;
    } catch (error) {
      logger.logUserAction("profile_update_failed", {
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  async resetPassword(email) {
    try {
      logger.logUserAction("password_reset_attempt", { email });
      const response = await apiClient.post("/users/reset-password", { email });
      logger.logUserAction("password_reset_success", { email });
      return response.data;
    } catch (error) {
      logger.logUserAction("password_reset_failed", {
        email,
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  async getAllSports(params = {}) {
    try {
      const response = await apiClient.get("/sports", {
        params,
      });
      logger.info("Sports fetched", {
        count: response.data?.data?.length || 0,
      });
      return response.data;
    } catch (error) {
      logger.error("Failed to fetch sports", {
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  async getSportById(id) {
    try {
      const response = await apiClient.get(`/sports/${id}`);
      logger.info("Sport fetched", {
        sportId: id,
        sportName: response.data?.data?.name,
      });
      return response.data;
    } catch (error) {
      logger.error("Failed to fetch sport", {
        sportId: id,
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  // =====================================================
  // OAUTH AUTHENTICATION
  // =====================================================

  async googleSignIn(idToken) {
    try {
      logger.logUserAction("google_signin_attempt");
      const response = await apiClient.post("/auth/google-signin", {
        idToken,
      });
      logger.logUserAction("google_signin_success");
      return response.data;
    } catch (error) {
      logger.logUserAction("google_signin_failed", {
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  // The server verifies the token with Facebook and reads the profile itself
  async facebookSignIn(accessToken) {
    try {
      logger.logUserAction("facebook_signin_attempt", {});
      const response = await apiClient.post("/auth/facebook-signin", {
        accessToken,
      });
      logger.logUserAction("facebook_signin_success", {});
      return response.data;
    } catch (error) {
      logger.logUserAction("facebook_signin_failed", {
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  // =====================================================
  // PHONE OTP AUTHENTICATION
  // =====================================================

  async requestPhoneOTP(phone, countryCode, type = "signup") {
    try {
      logger.logUserAction("otp_request_attempt", {
        phone: `${countryCode}${phone}`,
        type,
      });
      const response = await apiClient.post("/auth/phone/request-otp", {
        phone: `${countryCode}${phone}`,
        countryCode,
        type,
      });
      logger.logUserAction("otp_request_success", {
        phone: `${countryCode}${phone}`,
        type,
      });
      return response.data;
    } catch (error) {
      logger.logUserAction("otp_request_failed", {
        phone: `${countryCode}${phone}`,
        type,
        error: error.response?.data?.message,
      });
      throw error;
    }
  },

  async verifyPhoneOTP(phone, countryCode, code, type = "signup", userData = {}) {
    try {
      logger.logUserAction("otp_verify_attempt", {
        phone: `${countryCode}${phone}`,
        type,
      });
      const response = await apiClient.post("/auth/phone/verify-otp", {
        phone: `${countryCode}${phone}`,
        countryCode,
        code,
        type,
        userData,
      });
      logger.logUserAction("otp_verify_success", {
        phone: `${countryCode}${phone}`,
        type,
      });
      return response.data;
    } catch (error) {
      logger.logUserAction("otp_verify_failed", {
        phone: `${countryCode}${phone}`,
        type,
        error: error.response?.data?.message,
      });
      throw error;
    }
  },
};

export { apiClient };
export default apiService;
